import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Error,
  Warning,
  Schedule,
  ExpandMore,
  ExpandLess,
  Storage,
  Speed,
  TrendingUp,
} from '@mui/icons-material';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { format, formatDistanceToNow } from 'date-fns';

interface ETLRun {
  id: number;
  run_id: string;
  run_type: string;
  target_date: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
  total_stores: number;
  stores_success: number;
  stores_failed: number;
  total_sales: number;
  total_orders: number;
  error_message: string | null;
  failed_store_count: number;
  no_data_count: number;
}

interface StoreResult {
  id: number;
  store_id: string;
  store_name: string;
  source_system: string;
  status: string;
  total_sales: number | null;
  order_count: number | null;
  error_message: string | null;
  processed_at: string;
}

interface ETLStats {
  summary: {
    total_runs: number;
    successful_runs: number;
    failed_runs: number;
    partial_runs: number;
    avg_duration: number;
    last_run: string;
    total_store_failures: number;
  };
  problematicStores: {
    store_id: string;
    store_name: string;
    source_system: string;
    failure_count: number;
    no_data_count: number;
    last_attempt: string;
  }[];
}

const statusColors: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  completed: 'success',
  failed: 'error',
  partial: 'warning',
  started: 'info',
  no_data: 'warning',
  success: 'success',
};

const statusIcons: Record<string, React.ReactElement> = {
  completed: <CheckCircle fontSize="small" />,
  failed: <Error fontSize="small" />,
  partial: <Warning fontSize="small" />,
  started: <Schedule fontSize="small" />,
  success: <CheckCircle fontSize="small" />,
  no_data: <Warning fontSize="small" />,
};

const runTypeLabels: Record<string, string> = {
  scheduled: 'Daily Scheduled',
  pre_email: 'Pre-Email Sync',
  evening_catchup: 'Evening Catch-up',
  quick_sync: 'Quick Sync',
  manual: 'Manual',
};

const ETLMonitoring: React.FC = () => {
  const [runs, setRuns] = useState<ETLRun[]>([]);
  const [stats, setStats] = useState<ETLStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [runDetails, setRunDetails] = useState<Record<string, StoreResult[]>>({});
  const [tabValue, setTabValue] = useState(0);

  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

  const fetchData = useCallback(async () => {
    try {
      const [runsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/etl/logs?limit=30`),
        axios.get(`${API_BASE_URL}/etl/logs/stats`),
      ]);
      setRuns(runsRes.data.runs || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch ETL data:', error);
      enqueueSnackbar('Failed to fetch ETL logs', { variant: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleExpandRun = async (runId: string) => {
    if (expandedRun === runId) {
      setExpandedRun(null);
      return;
    }

    setExpandedRun(runId);

    if (!runDetails[runId]) {
      try {
        const response = await axios.get(`${API_BASE_URL}/etl/logs/${runId}`);
        setRunDetails(prev => ({ ...prev, [runId]: response.data.stores || [] }));
      } catch (error) {
        enqueueSnackbar('Failed to fetch run details', { variant: 'error' });
      }
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return '-';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="white">
            ETL Monitoring
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor data sync status and troubleshoot issues
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{ background: 'linear-gradient(135deg, #af2326, #c94144)' }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      {stats?.summary && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Storage sx={{ color: '#af2326' }} />
                  <Typography variant="body2" color="text.secondary">Total Runs (7d)</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="white">
                  {stats.summary.total_runs || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CheckCircle sx={{ color: '#4caf50' }} />
                  <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="white">
                  {stats.summary.total_runs > 0
                    ? `${Math.round((stats.summary.successful_runs / stats.summary.total_runs) * 100)}%`
                    : '-'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Speed sx={{ color: '#2196f3' }} />
                  <Typography variant="body2" color="text.secondary">Avg Duration</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="white">
                  {formatDuration(Math.round(stats.summary.avg_duration || 0))}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Error sx={{ color: '#f44336' }} />
                  <Typography variant="body2" color="text.secondary">Store Failures (7d)</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="white">
                  {stats.summary.total_store_failures || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{ mb: 2, '& .MuiTab-root': { color: 'text.secondary' }, '& .Mui-selected': { color: '#af2326' } }}
      >
        <Tab label="Recent Runs" />
        <Tab label="Problematic Stores" />
      </Tabs>

      {/* Recent Runs Tab */}
      {tabValue === 0 && (
        <TableContainer component={Paper} sx={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Time</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Duration</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Stores</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Sales</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No ETL runs recorded yet</Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Logs will appear here after the ETL tables are initialized and ETL runs complete.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((run) => (
                  <React.Fragment key={run.run_id}>
                    <TableRow hover sx={{ '&:hover': { background: 'rgba(255,255,255,0.02)' } }}>
                      <TableCell>
                        <Typography variant="body2" color="white">
                          {format(new Date(run.started_at), 'MMM d, h:mm a')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(run.started_at), { addSuffix: true })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={runTypeLabels[run.run_type] || run.run_type}
                          size="small"
                          sx={{
                            background: run.run_type === 'pre_email' ? 'rgba(33, 150, 243, 0.2)' : 'rgba(255,255,255,0.1)',
                            color: run.run_type === 'pre_email' ? '#64b5f6' : 'text.secondary',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="white">
                          {run.target_date}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusIcons[run.status]}
                          label={run.status.charAt(0).toUpperCase() + run.status.slice(1)}
                          size="small"
                          color={statusColors[run.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="white">
                          {formatDuration(run.duration_seconds)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" color="white">
                            {run.stores_success}/{run.total_stores}
                          </Typography>
                          {run.stores_failed > 0 && (
                            <Typography variant="caption" color="error.main">
                              {run.stores_failed} failed
                            </Typography>
                          )}
                          {run.no_data_count > 0 && (
                            <Typography variant="caption" color="warning.main" display="block">
                              {run.no_data_count} no data
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="white">
                          {formatCurrency(run.total_sales)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandRun(run.run_id)}
                          sx={{ color: 'text.secondary' }}
                        >
                          {expandedRun === run.run_id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Details */}
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedRun === run.run_id ? undefined : 'none' }}>
                        <Collapse in={expandedRun === run.run_id}>
                          <Box sx={{ py: 2, px: 2, background: 'rgba(0,0,0,0.2)', borderRadius: 1, my: 1 }}>
                            {run.error_message && (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                {run.error_message}
                              </Alert>
                            )}

                            {runDetails[run.run_id] ? (
                              runDetails[run.run_id].length > 0 ? (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ color: 'text.secondary' }}>Store</TableCell>
                                      <TableCell sx={{ color: 'text.secondary' }}>System</TableCell>
                                      <TableCell sx={{ color: 'text.secondary' }}>Status</TableCell>
                                      <TableCell sx={{ color: 'text.secondary' }}>Sales</TableCell>
                                      <TableCell sx={{ color: 'text.secondary' }}>Orders</TableCell>
                                      <TableCell sx={{ color: 'text.secondary' }}>Error</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {runDetails[run.run_id].slice(0, 20).map((store) => (
                                      <TableRow key={store.id}>
                                        <TableCell>
                                          <Typography variant="body2" color="white">{store.store_name}</Typography>
                                          <Typography variant="caption" color="text.secondary">{store.store_id}</Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={store.source_system}
                                            size="small"
                                            sx={{
                                              background: store.source_system === 'par' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(33, 150, 243, 0.2)',
                                              color: store.source_system === 'par' ? '#ce93d8' : '#64b5f6',
                                              textTransform: 'uppercase',
                                              fontSize: '0.7rem',
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            icon={statusIcons[store.status]}
                                            label={store.status}
                                            size="small"
                                            color={statusColors[store.status] || 'default'}
                                          />
                                        </TableCell>
                                        <TableCell>{formatCurrency(store.total_sales)}</TableCell>
                                        <TableCell>{store.order_count || '-'}</TableCell>
                                        <TableCell>
                                          {store.error_message && (
                                            <Typography variant="caption" color="error.main">
                                              {store.error_message.substring(0, 50)}...
                                            </Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography color="text.secondary" textAlign="center" py={2}>
                                  No store details available for this run
                                </Typography>
                              )
                            ) : (
                              <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={24} />
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Problematic Stores Tab */}
      {tabValue === 1 && (
        <TableContainer component={Paper} sx={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Store</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>System</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Failures (7d)</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>No Data (7d)</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Last Attempt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats?.problematicStores?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                    <Typography color="text.secondary">All stores are syncing successfully!</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                stats?.problematicStores?.map((store) => (
                  <TableRow key={store.store_id} hover>
                    <TableCell>
                      <Typography variant="body2" color="white">{store.store_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{store.store_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={store.source_system}
                        size="small"
                        sx={{
                          background: store.source_system === 'par' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(33, 150, 243, 0.2)',
                          color: store.source_system === 'par' ? '#ce93d8' : '#64b5f6',
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={store.failure_count}
                        size="small"
                        color={store.failure_count > 5 ? 'error' : store.failure_count > 0 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={store.no_data_count}
                        size="small"
                        color={store.no_data_count > 10 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDistanceToNow(new Date(store.last_attempt), { addSuffix: true })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ETLMonitoring;
